import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Paper,
  Divider,
  MenuItem,
  TextField,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { Images } from "../../../utils/Images";
import AuthenticationTextInput from "../../common/TextInput/AuthenticationTextInput";
import {
  sanitizeFormFieldValue,
  validateIndianMobile,
} from "../../../utils/phoneValidation";

const ProfileEdit = ({ userData, onSave, onCancel }) => {

  const [formData, setFormData] = useState(userData);
  const [errors, setErrors] = useState({});
  const theme = useTheme();
        const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
      const isBelow1200 = useMediaQuery("(max-width:1199px)");
      const isBelow1023 = useMediaQuery("(max-width:1023px)");

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleDobChange = (event) => {
    let value = event.target.value;
    
    // Remove all non-numeric characters
    value = value.replace(/\D/g, '');
    
    // Limit to 8 digits (DDMMYYYY)
    if (value.length > 8) {
      value = value.slice(0, 8);
    }
    
    // Format as DD-MM-YYYY
    let formattedValue = '';
    if (value.length > 0) {
      formattedValue = value.slice(0, 2);
      if (value.length > 2) {
        formattedValue += '-' + value.slice(2, 4);
        if (value.length > 4) {
          formattedValue += '-' + value.slice(4, 8);
        }
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      dob: formattedValue,
    }));
    
    // Clear error when user starts typing
    if (errors.dob) {
      setErrors((prev) => ({
        ...prev,
        dob: "",
      }));
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData((prev) => ({
          ...prev,
          profilePicture: e.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    const phoneCheck = validateIndianMobile(formData.phone);
    if (!phoneCheck.isValid) {
      newErrors.phone = phoneCheck.error;
    }

    // DOB validation
    if (!formData.dob.match(/^\d{2}-\d{2}-\d{4}$/)) {
      newErrors.dob = "Please enter date in DD-MM-YYYY format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        ...formData,
        phone: validateIndianMobile(formData.phone).digits,
      });
    }
  };

  return (
    <>
    <Box sx={{ position: "relative", display: isMobile?"block":"none",mb:"20px" ,width:"fit-content",mx:"auto", }}>
          <Avatar
            src={formData.profilePicture || Images.defaultAvatar}
            sx={{ width:isMobile?"100px": 80, height:isMobile?"100px": 80 }}
          />
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="profile-picture-upload"
            type="file"
            onChange={handleImageChange}
          />
          <label htmlFor="profile-picture-upload">
            <IconButton
              component="span"
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                bgcolor: "#00AD40",
                color: "white",
                "&:hover": { bgcolor: "#00AD40" },
                width: isMobile?"34px": "100%",
                height: isMobile?"34px":"100%",
                opacity:isMobile?"1": 0,
              }}
            >
              <PhotoCameraIcon fontSize="small" />
            </IconButton>
          </label>
        </Box>

    <Box sx={{ bgcolor: "white", borderRadius: "16px 16px 12px 12px",border:" 1px solid #DBE0E5",overflow:"hidden" ,width:"100%" }}>
      
    <Paper elevation={0} sx={{ p: isBelow1023?"18px 16px": "30px", mx: "auto" }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "600",
          mb:isMobile?"0": "20px",
          fontSize: "16px",
          lineHeight: "21px",
          color: "#000",
        }}
      >
       Edit Profile Details
      </Typography>

      {/* Profile Picture Upload */}
      <Box sx={{ display: isMobile?"none":"flex", alignItems: "center", gap: "16px" }}>
        <Box sx={{ position: "relative"  }}>
          <Avatar
            src={formData.profilePicture || Images.defaultAvatar}
            sx={{ width: 80, height: 80 }}
          />
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="profile-picture-upload"
            type="file"
            onChange={handleImageChange}
          />
          <label htmlFor="profile-picture-upload">
            <IconButton
              component="span"
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
                width: "100%",
                height: "100%",
                opacity: 0,
              }}
            >
              <PhotoCameraIcon fontSize="small" />
            </IconButton>
          </label>
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "#fff",
              fontSize: "12px",
              mb: "7px",
              p: "4px 6px",
              backgroundColor: "#29CC6A",
              borderRadius: "5px",
              fontWeight: "700",
              textTransform: "uppercase",
              width: "fit-content",
            }}
          >
            ID {userData.id}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "700",
              color: "#1D1D1B",
              fontSize: "18px",
              mb: "2px",
            }}
          >
            {userData.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#686866",
              textTransform: "uppercase",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            {userData.role}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my:isMobile?"16px": "30px", borderColor: "#9FA2A7" }} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:isMobile?"repeat(auto-fit, minmax(200px, 1fr))": "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "20px",
          rowGap: "6px",
        }}
      >
        {/* Name */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: "8px",
              fontWeight: "600",
              fontSize: "14px",
              color: "#141414",
            }}
          >
            Name
          </Typography>
          <AuthenticationTextInput
            value={formData.name}
            onChange={handleChange("name")}
            error={errors.name}
            helperText={errors.name}
            placeholder="Enter your name"
            required
          />
        </Box>
        {/* Email */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: "8px",
              fontWeight: "600",
              fontSize: "14px",
              color: "#141414",
            }}
          >
            Email
          </Typography>
          <AuthenticationTextInput
            value={formData.email}
            onChange={handleChange("email")}
            error={errors.email}
            helperText={errors.email}
            placeholder="Enter your email"
            type="email"
            required
          />
        </Box>
        {/* Phone Number */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: "8px",
              fontWeight: "600",
              fontSize: "14px",
              color: "#141414",
            }}
          >
            Phone number
          </Typography>
          <AuthenticationTextInput
            value={formData.phone}
            onChange={handleChange("phone")}
            error={errors.phone}
            helperText={errors.phone}
            placeholder="Enter Phone Number"
            phoneInput
          />
        </Box>
        {/* DOB  */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: "8px",
              fontWeight: "600",
              fontSize: "14px",
              color: "#141414",
            }}
          >
            DOB
          </Typography>
          <AuthenticationTextInput
            value={formData.dob}
            onChange={handleDobChange}
            error={errors.dob}
            helperText={errors.dob}
            placeholder="DD-MM-YYYY"
            maxLength={10}
          />
        </Box>
        {/* Passport Number */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: "8px",
              fontWeight: "600",
              fontSize: "14px",
              color: "#141414",
            }}
          >
            Passport Number
          </Typography>
          <AuthenticationTextInput
            value={formData.passport}
            onChange={handleChange("passport")}
            error={errors.passport}
            helperText={errors.passport}
            placeholder="A1234567"
            inputProps={{ style: { textTransform: "capitalize" } }}
          />
        </Box>
        {/* PAN Number */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: "8px",
              fontWeight: "600",
              fontSize: "14px",
              color: "#141414",
            }}
          >
            PAN Number
          </Typography>
          <AuthenticationTextInput
            value={formData.pan}
            onChange={handleChange("pan")}
            error={errors.pan}
            helperText={errors.pan}
            placeholder="ABCDE1234F"
            inputProps={{ style: { textTransform: "capitalize" } }}
          />
        </Box>

        <Box>
          {/* Use regular TextField for select input */}
          <Typography
            variant="subtitle2"
            sx={{
              mb: "8px",
              fontWeight: "600",
              fontSize: "14px",
              color: "#141414",
             
            }}
          >
            Gender
          </Typography>
          <TextField
            select
            fullWidth
            value={formData.gender}
            onChange={handleChange("gender")}
            variant="outlined"
            sx={{
               height:isMobile?"45px":"52px",
              "& .MuiOutlinedInput-root": {
                 height:"100%",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "rgb(185, 185, 184)",
                  padding: isMobile?"6px":"10px"
                },
                "&:hover fieldset": {
                  borderColor: "rgb(185, 185, 184)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgb(185, 185, 184)",
                },
              },
            }}
          >
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
        </Box>

        {/* Action Buttons */}
      </Box>
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end",marginTop:"20px" }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            color: "#fff",
            border: "unset",
            backgroundColor: "#1D1D1B",
            padding: "12px 24px",
            fontSize: "12px",
            fontWeight: "500",
            borderRadius: "8px",
            minWidth: "88px",
            boxShadow: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            color: "#fff",
            border: "unset",
            backgroundColor: "#F04951",
            padding: "12px 24px",
            fontSize: "12px",
            fontWeight: "500",
            borderRadius: "8px",
            minWidth: "88px",
            boxShadow: "none",
          }}
        >
          Save
        </Button>
      </Box>
    </Paper>
    </Box>
    </>
  );
};

export default ProfileEdit;
